// Import padrão de axios (ESM). O Babel/Webpack do projeto faz a interop com CJS automaticamente.
// Isso evita problemas em runtime como "axios.create is not a function".
import axios from 'axios';
import { CAMPINA_API, PATOS_API } from './InstanceAPI';

/**
 * Obtém a unidade do usuário logado
 * @returns {string} Unidade do usuário ('CAMPINA GRANDE' ou 'PATOS')
 */
const getUserUnit = () => {
  try {
    const storageData = sessionStorage.getItem("loggedUser");
    if (storageData) {
      const user = JSON.parse(storageData);
      return user.unit || 'PATOS';
    }
  } catch (error) {
    // Erro silencioso ao obter unidade
  }
  return 'PATOS'; // Default
};

/**
 * Obtém a URL base da API baseada na unidade do usuário
 * Usa a mesma lógica do InstanceAPI.js para garantir consistência
 * @returns {string} URL base da API
 */
const getBaseURL = () => {
  const unit = getUserUnit();
  const isCampina = unit === "CAMPINA GRANDE";

  // Usa a URL hardcoded baseada na unidade
  const BASE_URL = isCampina ? CAMPINA_API : PATOS_API;

  return BASE_URL;
};

/**
 * Obtém o token de autenticação do usuário logado
 * @returns {string|null} Token JWT ou null
 */
const getAuthToken = () => {
  try {
    const storageData = sessionStorage.getItem("loggedUser");
    if (storageData) {
      const user = JSON.parse(storageData);
      return user.token || null;
    }
  } catch (error) {
    // Erro silencioso ao obter token
  }
  return null;
};

/**
 * Cria uma instância do axios configurada para a API Autentico
 * @returns {AxiosInstance} Instância do axios configurada
 */
export const createApiClient = () => {
  const instance = axios.create({
    baseURL: getBaseURL(),
    timeout: 300000, // 5 minutos para uploads grandes
    maxContentLength: 500 * 1024 * 1024, // 500MB
    maxBodyLength: 500 * 1024 * 1024, // 500MB
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Adiciona token de autenticação
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Atualiza baseURL caso a unidade tenha mudado
      config.baseURL = getBaseURL();
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Trata erros globalmente
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // Tratamento de erros HTTP
      if (error.response) {
        // O servidor respondeu com um status de erro
        const { status, data } = error.response;
        
        // Erro 429 — pode ser throttle local do backend ou bloqueio HTTP da Sponte (payload inclui `source`)
        if (status === 429) {
          const waitTime = data?.waitTime || 120;
          const minutes = Math.ceil(waitTime / 60);
          const seconds = waitTime % 60;
          const timeMessage = minutes > 1 
            ? `${minutes} minutos e ${seconds} segundos`
            : `${waitTime} segundos`;

          const isLocalThrottle = data?.source === 'local';

          // Throttle interno do servidor: não toast, não overlay, não evento global —
          // só falha silenciosa para o chamador (evita poluir o fluxo).
          if (isLocalThrottle) {
            return Promise.reject(error);
          }

          const errorMessage =
            `⚠️ API Sponte bloqueada!\n\n${data?.message || 'O limite de requisições foi atingido na API Sponte.'}\n\n⏱️ Aguarde ${timeMessage} antes de tentar novamente.\n\nPor favor, não continue tentando enquanto o bloqueio estiver ativo.`;

          const blockedUntil = Date.now() + (waitTime * 1000);
          localStorage.setItem('rateLimitBlock', JSON.stringify({
            blockedUntil,
            waitTime
          }));

          const rateLimitEvent = new CustomEvent('rateLimitExceeded', {
            detail: {
              message: errorMessage,
              waitTime,
              timeMessage,
              status: 429,
              source: data?.source,
              blockFullScreen: true,
              sponteBlocked: data?.sponteBlocked === true,
            }
          });
          window.dispatchEvent(rateLimitEvent);
          
        }
        
        // Erro 401 - Não autorizado (token inválido/expirado)
        if (status === 401) {
          // Limpar sessão e redirecionar para login
          sessionStorage.removeItem("loggedUser");
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
        
      }
      
      // Adiciona informações úteis ao erro para facilitar tratamento
      // Não tenta modificar propriedades read-only do erro
      // O axios já marca erros com isAxiosError automaticamente
      
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Instância padrão do cliente API
 * Use esta instância para todas as requisições
 */
export const apiClient = createApiClient();

/**
 * Função helper para criar uma nova instância (útil para testes)
 * @returns {AxiosInstance} Nova instância do axios
 */
export const createNewApiClient = () => {
  return createApiClient();
};


